import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import classNames from 'classnames';
import SanitizedHTML from 'react-sanitized-html';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown, faAngleUp } from '@fortawesome/pro-solid-svg-icons';

import Attachments from 'bundles/apex/activities/attachments';
import LoadingSpinner from 'components/loading_spinner';
import TaggedBody from 'bundles/apex/deals/tagged_body';
import useResizeObserver from 'hooks/use_resize_observer';
import { get } from 'helpers/api';

export default function ActivityBody({ activity }) {
  const [loadedBodyParts, setLoadedBodyParts] = useState(null);
  const [loading, setLoading] = useState(false);
  const [truncated, setTruncated] = useState(true);
  const [hasOverflow, setHasOverflow] = useState();
  const bodyRef = useResizeObserver(ref => setHasOverflow(ref.current.scrollHeight > ref.current.clientHeight));

  useEffect(() => {
    const element = bodyRef.current;
    if (element) {
      setHasOverflow(element.scrollHeight > element.clientHeight);
      setTruncated(true);
    }
  }, [activity]);

  async function showMore() {
    if (activity.hasCrmEmail && !loadedBodyParts) {
      setLoading(true);
      setLoadedBodyParts((await get(activity.path)).bodyParts);
      setLoading(false);
    }

    setTruncated(false);
  }

  function showLess() {
    setTruncated(true);
  }

  let body;
  if (activity.hasCrmEmail && loadedBodyParts && !truncated) {
    // Activities created from an email will only have one body part. However, those that were
    // migrated from premium marketing tasks with an email body may have tags preceeding the HTML.
    // These must be split up and rendered separately. Note that email activities can't be edited,
    // so there is no risk of tags being added after the HTML.
    body = <React.Fragment>
      <TaggedBody bodyParts={loadedBodyParts.slice(0, loadedBodyParts.length - 1)} />
      <SanitizedHTML className="fade-lock" html={loadedBodyParts[loadedBodyParts.length - 1]} />
    </React.Fragment>;
  } else {
    body = <TaggedBody bodyParts={activity.bodyParts} />;
  }

  const toggle = truncated ? showMore : showLess;
  const canToggle = activity.hasCrmEmail || hasOverflow || !truncated;

  return (
    <div className="d-flex">
      <div className="flex-grow-1 mr-5 max-full-width">
        <div className={classNames('activity-body mb-4', { truncated })} ref={bodyRef}>
          {body}
        </div>

        {canToggle &&
          <a aria-label="toggle" className="clickable" onClick={loading ? null : toggle}>
            <span className="mr-2">
              {truncated ? 'View More' : 'View Less'}
            </span>
            {loading
              ? <LoadingSpinner inline size="sm" skipTopBuffer />
              : <FontAwesomeIcon icon={truncated ? faAngleDown : faAngleUp} />
            }
          </a>
        }

        {activity.attachments && activity.attachments.length > 0 && (
          <Attachments attachments={activity.attachments} />
        )}
      </div>
    </div>
  );
}

ActivityBody.propTypes = {
  activity: PropTypes.shape({
    attachments: PropTypes.array,
    bodyParts: PropTypes.array.isRequired,
    hasCrmEmail: PropTypes.bool,
    path: PropTypes.string.isRequired,
  }).isRequired,
};
